import { IAPIRoutes } from "../../../../config";

export type INavigatorRepository = ReturnType<
  typeof NavigatorRepositoryFactory
>;

export const NavigatorRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}) => {
  const { GET, POST, PUT, DELETE, PATCH } = APIProvider;
  const { getAuthenticationToken } = AuthService;

  const createInitialVisitAPI: ICreateInitialVisitAPI = async (payload) => {
    const url = APIRoutes.apiPostIPVNote
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());

    const dataToSend = {
      type: payload.type,
      shortlisted: payload.shortlisted,
      notes: payload.notes,
      file_link: payload.file_link,
      total_time: payload.total_time,
      context: payload.context,
      doc_type: payload.doc_type,
    };

    return POST(url, dataToSend, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const getIPVNotesByStatusAPI: IGetIPVNotesByStatusAPI = async (payload) => {
    const url = APIRoutes.apiGetIPVNotesByClinicID.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );

    const params: Record<string, number | undefined> = {
      status: payload.status,
    };

    return GET(url, params, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const getIPVNotesByStatusAPIv3: IGetIPVNotesByStatusAPIv3 = async (
    payload
  ) => {
    const url = APIRoutes.apiGetIPVNotesByClinicIDv3.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );

    const params: Record<string, number | undefined> = {
      note_type: payload.status,
    };

    return GET(url, params, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const putIPVNoteAPI: IPutIPVNoteAPI = async (payload) => {
    const url = APIRoutes.apiPutIPVNote
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString())
      .replace("{ipv_note_id}", payload.ipv_note_id.toString());

    const dataToSend = {
      type: payload.type,
      shortlisted: payload.shortlisted,
      notes: payload.notes,
      file_link: payload.file_link,
      total_time: payload.total_time,
      context: payload.context,
      doc_type: payload.doc_type,
    };

    return PUT(url, dataToSend, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const getIPVNotesByPatientIDAPI: IGetIPVNotesByPatientIDAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiGetIPVNotesByPatientID
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());

    return GET(
      url,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
  };

  const deleteIPVNoteAPI: IDeleteIPVNoteAPI = async (payload) => {
    const url = APIRoutes.apiDeleteIPVNote
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString())
      .replace("{ipv_note_id}", payload.ipv_note_id.toString());

    return DELETE(
      url,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
  };

  const deleteIPVDraftAndSessionAPI: IDeleteIPVDraftAndSessionAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiDeleteIPVDraftAndSession
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());

    const dataToSend: {
      ipv_note_id?: number;
      session_id?: string;
    } = {};

    if (payload.ipv_note_id) {
      dataToSend.ipv_note_id = payload.ipv_note_id;
    }
    if (payload.session_id) {
      dataToSend.session_id = payload.session_id;
    }

    return DELETE(url, dataToSend, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const getPatientsForNavigatorAPI: IGetPatientsForNavigatorAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiGetPatientsForNavigator.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );
    const queryStringParameters: Record<string, string | boolean> = {};
    if (payload.bypass_ipv_gate) {
      queryStringParameters.bypass_ipv_gate = true;
    }
    return GET(url, queryStringParameters, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const getNavigatorPatientByIdAPI: IGetNavigatorPatientByIdAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiGetNavigatorPatientById
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());
    return GET(
      url,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
  };

  const getCustomTodoVisitsAPI: IGetCustomTodoVisitsAPI = async (payload) => {
    const baseUrl = APIRoutes.apiGetCustomTodoVisits.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );
    const params = new URLSearchParams({ state: payload.state });
    return GET(
      `${baseUrl}?${params.toString()}`,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
  };

  const updatePatientExtraInfoAPI: IUpdatePatientExtraInfoAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiPostPatientExtraInfo
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());

    const dataToSend: Record<string, unknown> = {};
    if (payload.extra_info !== undefined) {
      dataToSend.extra_info = payload.extra_info;
    }
    if (payload.patient_info !== undefined) {
      dataToSend.patient_info = payload.patient_info;
    }

    return POST(url, dataToSend, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const getPatientVisitNotesAPI: IGetPatientVisitNotesAPI = async (payload) => {
    const url = APIRoutes.apiGetPatientVisitNotes
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());
    return GET(
      url,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
  };

  const storePreChartNoteAPI: IStorePreChartNoteAPI = async (payload) => {
    const url = APIRoutes.apiStorePreChartNote.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );
    return PUT(
      url,
      {
        apptNote: payload.apptNote,
        patient_id: payload.patient_id,
      },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
  };

  const getAtRiskReviewLockStatusAPI = async (payload: {
    clinic_id: number;
  }) => {
    const url = APIRoutes.apiGetAtRiskReviewLockStatus.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );
    return GET(
      url,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
  };

  const postAtRiskReviewOverrideRequestAPI = async (payload: {
    clinic_id: number;
    reasons: { patient_id: number; reason: string }[];
  }) => {
    const url = APIRoutes.apiPostAtRiskReviewOverrideRequest.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );
    return POST(
      url,
      { reasons: payload.reasons },
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
  };

  const patchAtRiskReviewOverrideRequestAPI = async (payload: {
    clinic_id: number;
    reasons: { patient_id: number; reason: string }[];
  }) => {
    const url = APIRoutes.apiPatchAtRiskReviewOverrideRequest.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );
    return PATCH(
      url,
      { reasons: payload.reasons },
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
  };

  const patchNavigatorIpvIneligibleAPI = async (payload: {
    clinic_id: number;
    patient_id: number;
    admission_id: number;
    navigator_ipv_ineligible?: boolean;
    is_excluded?: boolean | null;
    exclude_reason?: string | null;
  }) => {
    const url = APIRoutes.apiPatchNavigatorIpvIneligible
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString())
      .replace("{admission_id}", payload.admission_id.toString());

    const body: Record<string, unknown> = {};
    if (payload.navigator_ipv_ineligible !== undefined) {
      body.navigator_ipv_ineligible = payload.navigator_ipv_ineligible;
    }
    if (payload.is_excluded !== undefined) {
      body.is_excluded = payload.is_excluded;
    }
    if (payload.exclude_reason !== undefined) {
      body.exclude_reason = payload.exclude_reason;
    }

    return PATCH(url, body, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const createCustomTodoAPI: ICreateCustomTodoAPI = async (payload) => {
    const url = APIRoutes.apiPostCustomTodo
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());

    const body: Record<string, unknown> = { reasons: payload.reasons };
    if (payload.admission_id !== undefined && payload.admission_id !== null) {
      body.admission_id = payload.admission_id;
    }

    return POST(url, body, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const createCustomTodoRecurrenceAPI: ICreateCustomTodoRecurrenceAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiCustomTodoRecurrence
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());

    const body: Record<string, unknown> = {
      reasons: payload.reasons,
      cadence_days: payload.cadence_days,
      start_date: payload.start_date,
    };
    if (payload.end_date !== undefined && payload.end_date !== null) {
      body.end_date = payload.end_date;
    }
    if (payload.admission_id !== undefined && payload.admission_id !== null) {
      body.admission_id = payload.admission_id;
    }

    return POST(url, body, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const getCustomTodoRecurrencesAPI: IGetCustomTodoRecurrencesAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiCustomTodoRecurrence
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());

    return GET(
      url,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
  };

  const getClinicCustomTodoRecurrencesAPI: IGetClinicCustomTodoRecurrencesAPI =
    async (payload) => {
      const baseUrl = APIRoutes.apiGetClinicCustomTodoRecurrences.replace(
        "{clinic_id}",
        payload.clinic_id.toString()
      );
      const params = new URLSearchParams();
      if (payload.include_inactive) {
        params.set("include_inactive", "true");
      }
      const query = params.toString();

      return GET(
        query ? `${baseUrl}?${query}` : baseUrl,
        {},
        { Authorization: `Bearer ${await getAuthenticationToken()}` }
      );
    };

  const deleteCustomTodoRecurrenceAPI: IDeleteCustomTodoRecurrenceAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiCustomTodoRecurrenceById
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString())
      .replace("{schedule_id}", payload.schedule_id.toString());

    return DELETE(
      url,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
  };

  const updateCustomTodoAPI: IUpdateCustomTodoAPI = async (payload) => {
    const url = APIRoutes.apiPatchCustomTodo
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString())
      .replace("{todo_id}", payload.todo_id.toString());

    // Backend requires at least one field; explicitly send `null` for ipv_note_id
    // to unlink, but omit the key entirely when the caller didn't pass it.
    const body: Record<string, unknown> = {};
    if (payload.reasons !== undefined) {
      body.reasons = payload.reasons;
    }
    if (Object.prototype.hasOwnProperty.call(payload, "ipv_note_id")) {
      body.ipv_note_id = payload.ipv_note_id;
    }

    return PATCH(url, body, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const deleteCustomTodoAPI: IDeleteCustomTodoAPI = async (payload) => {
    const url = APIRoutes.apiDeleteCustomTodo
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString())
      .replace("{todo_id}", payload.todo_id.toString());

    return DELETE(
      url,
      {},
      { Authorization: `Bearer ${await getAuthenticationToken()}` }
    );
  };

  return {
    createInitialVisitAPI,
    getIPVNotesByStatusAPI,
    putIPVNoteAPI,
    getIPVNotesByPatientIDAPI,
    deleteIPVNoteAPI,
    deleteIPVDraftAndSessionAPI,
    getIPVNotesByStatusAPIv3,
    getPatientsForNavigatorAPI,
    getNavigatorPatientByIdAPI,
    getCustomTodoVisitsAPI,
    updatePatientExtraInfoAPI,
    getPatientVisitNotesAPI,
    storePreChartNoteAPI,
    getAtRiskReviewLockStatusAPI,
    postAtRiskReviewOverrideRequestAPI,
    patchAtRiskReviewOverrideRequestAPI,
    patchNavigatorIpvIneligibleAPI,
    getCustomTodoRecurrencesAPI,
    getClinicCustomTodoRecurrencesAPI,
    createCustomTodoAPI,
    createCustomTodoRecurrenceAPI,
    deleteCustomTodoRecurrenceAPI,
    updateCustomTodoAPI,
    deleteCustomTodoAPI,
  };
};
